import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import { useMemo, useState } from "react";
import { Heart, Search } from "react-feather";
import { useLazyLoadQuery } from "react-relay";
import {
  NavigationPreviewQuery,
  NavigationPreviewQuery$data,
} from "./__generated__/NavigationPreviewQuery.graphql";
import { useMobilePreviewState } from "../../contexts/MobilePreviewContext";
import { getBrandIcon } from "../../utils/brandIcons";
import { convertFromNestedMenuGraphQL as convertMenuItems } from "../../views/dashboard/build_section/navigation/MenuCard";
import { NavigationItem } from "../../views/dashboard/build_section/navigation/ShortcutsCard";
import MobilePreviewBottomSheet from "./MobilePreviewBottomSheet";
import MobilePreviewSidepane from "./MobilePreviewSidepane";
import { getLiquidGlassColors, liquidGlassCapsuleSx } from "./liquidGlass";

const mapToNavigationItem = (
  item:
    | NavigationItem
    | NavigationPreviewQuery$data["brand"]["appConfig"]["navigationItems"][number]
): NavigationItem => {
  if (!item) return { label: "", url: "", localizations: [], children: [] };

  const mapChildren = (
    children: readonly NavigationItem[]
  ): NavigationItem[] => {
    return Array.from(children).map((child) => {
      return {
        label: child.label,
        url: child.url || "",
        localizations: child.localizations || [],
        children: child.children
          ? mapChildren(child.children as NavigationItem[])
          : [],
      };
    });
  };

  return {
    label: item.label,
    url: item.url || "",
    localizations: item.localizations || [],
    children: item.children
      ? mapChildren(item.children as NavigationItem[])
      : [],
  };
};

const query = graphql`
  query NavigationPreviewQuery($id: ID!) {
    brand(id: $id) {
      id
      appConfig {
        id
        buildTheme {
          buttonCartIcon
          logoUrl
        }

        buildNavigation {
          id
          isGorgiasEnabled
          isTabBarTitleEnabled
        }

        navigationItems {
          index
          label
          localizations {
            languageCode
            text
            url
          }
          url
          children {
            index
            label
            url
            localizations {
              languageCode
              text
              url
            }
            children {
              index
              label
              url
              localizations {
                languageCode
                text
                url
              }
            }
          }
        }

        nestedMenuItems {
          title
          localizations {
            languageCode
            text
            url
          }
          destinationUrl
          index
          children {
            title
            destinationUrl
            index
            localizations {
              languageCode
              text
              url
            }
            children {
              title
              destinationUrl
              index
              localizations {
                languageCode
                text
                url
              }
            }
          }
        }

        tabs {
          id
          type
          customName
          localizations {
            languageCode
            text
            url
          }
          customUrl
          icon
        }
      }
    }
  }
`;

const NavigationPreview = ({
  brandID,
  isLiquidGlass = false,
}: {
  brandID: string;
  isLiquidGlass?: boolean;
}) => {
  const theme = useTheme();
  const { navigation, theme: themePreview } = useMobilePreviewState();

  const data = useLazyLoadQuery<NavigationPreviewQuery>(query, {
    id: brandID,
  });
  const brand = data.brand;
  const appConfig = brand.appConfig;
  const buildTheme = appConfig.buildTheme;
  const navigationItems = appConfig.navigationItems;
  const isGorgiasEnabled = appConfig.buildNavigation.isGorgiasEnabled;
  const isTabBarTitleEnabled = appConfig.buildNavigation.isTabBarTitleEnabled;
  const items = useMemo(
    () => appConfig.nestedMenuItems ?? [],
    [appConfig.nestedMenuItems]
  );

  // Mobile States
  const defaultNestedMenuItems =
    navigation.nestedMenuItems ?? convertMenuItems(items);
  const defaultShortcutsPills = navigation.shortcutsPills ?? navigationItems;
  const defaultTabs =
    navigation.tabs ??
    brand.appConfig.tabs.map((tab) => {
      const { id, ...rest } = tab;
      return rest;
    });
  const defaultIsTitleEnabled =
    navigation.isTitleEnabled ?? isTabBarTitleEnabled;
  const defaultNavTitleMode = navigation.navTitleMode ?? "PAGE_TITLE";
  const defaultNavTitlePosition = navigation.navTitlePosition ?? "LEFT";
  const defaultIsGorgiasEnabled =
    navigation.isGorgiasEnabled ?? isGorgiasEnabled;

  // Theme States
  const defaultButtonCartIcon =
    themePreview.buttonCartIcon ?? buildTheme.buttonCartIcon ?? "SHOPPING_BAG";
  const logoUrl = themePreview.logoUrl ?? buildTheme.logoUrl;

  const CartIcon = getBrandIcon(defaultButtonCartIcon);

  const navBg = theme.palette.navigationBackground.main;
  const glassColors = getLiquidGlassColors(navBg);

  // Needed to skip animation on first render
  const [isSidepaneFirstRender, setSidepaneFirstRender] = useState(true);
  const [isBottomSheetFirstRender, setIsBottomSheetFirstRender] =
    useState(true);

  const [value, setValue] = useState(0);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isBottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [bottomSheetIndex, setBottomSheetIndex] = useState(-1);

  const titleContent =
    value === 0 || defaultNavTitleMode === "LOGO" ? (
      <img
        style={{ maxHeight: "36px", maxWidth: "130px" }}
        alt=""
        src={logoUrl ?? ""}
        loading="lazy"
      />
    ) : (
      <Typography variant="h6" sx={{ maxWidth: "130px", fontWeight: 800 }}>
        {defaultTabs[value].customName}
      </Typography>
    );

  const shortcutItems = defaultShortcutsPills.map((item, index) => {
    if (isLiquidGlass) {
      return (
        <Button
          key={index}
          sx={{
            ...liquidGlassCapsuleSx(glassColors, { padding: "8px 14px" }),
            "&:hover": {
              backgroundColor: glassColors.hoverFill,
            },
            flexShrink: 0,
            minWidth: 0,
            textTransform: "none",
          }}
          onClick={() => {
            if (item?.children && item.children.length > 0) {
              setBottomSheetIndex(index);
              setBottomSheetOpen(true);
              setIsBottomSheetFirstRender(false);
            }
          }}
        >
          <Typography
            sx={{
              color: glassColors.foreground,
              fontSize: "10px",
            }}
            variant="body0"
          >
            {item.label || "--"}
          </Typography>
        </Button>
      );
    }

    return (
      <Button
        key={index}
        sx={{
          backgroundColor: theme.palette.primaryPillBg.main,
          "&:hover": {
            backgroundColor: theme.palette.primaryPillBg.main + "CC",
          },
          p: "8px 12px",
          flexShrink: 0,
          borderColor: theme.palette.secondaryPillOutline.main,
          borderWidth:
            theme.palette.secondaryPillOutline.main === "transparent" ? 0 : 2,
          borderStyle: "solid",
        }}
        onClick={() => {
          if (item?.children && item.children.length > 0) {
            setBottomSheetIndex(index);
            setBottomSheetOpen(true);
            setIsBottomSheetFirstRender(false);
          }
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primaryPillFg.main,
            fontSize: "10px",
          }}
          variant="body0"
        >
          {item.label || "--"}
        </Typography>
      </Button>
    );
  });

  const glassHeader = (
    <Stack
      direction="column"
      width="100%"
      spacing={1}
      sx={{
        backgroundColor: navBg,
        color: glassColors.foreground,
        padding: "12px",
        width: "100%",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
        position="relative"
      >
        <IconButton
          onClick={() => {
            setMenuOpen(true);
            setSidepaneFirstRender(false);
          }}
          sx={{
            ...liquidGlassCapsuleSx(glassColors),
            width: 40,
            height: 40,
            "&:hover": {
              backgroundColor: glassColors.hoverFill,
            },
          }}
        >
          <MenuIcon />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            maxWidth: "160px",
            pointerEvents: "none",
          }}
        >
          {titleContent}
        </Box>
        <Stack direction="row" alignItems={"center"} spacing={0.75}>
          {(
            [
              ["search", <Search size={18} color="currentColor" />],
              ["wishlist", <Heart size={18} color="currentColor" />],
              ["cart", <CartIcon size={18} color="currentColor" />],
            ] as const
          ).map(([key, icon]) => (
            <IconButton
              key={key}
              sx={{
                ...liquidGlassCapsuleSx(glassColors),
                width: 40,
                height: 40,
                "&:hover": {
                  backgroundColor: glassColors.hoverFill,
                },
              }}
            >
              {icon}
            </IconButton>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );

  const standardHeader = (
    <Stack
      direction="column"
      width="100%"
      spacing={1}
      sx={{
        backgroundColor: theme.palette.navigationBackground.main,
        color: theme.palette.navigationForeground.main,
        padding: "12px",
        width: "100%",
        borderBottom: "1px solid #F1F1F1",
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}
    >
      <Stack
        direction="row"
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <Stack direction="row" alignItems={"center"} spacing={2}>
          <IconButton
            onClick={() => {
              setMenuOpen(true);
              setSidepaneFirstRender(false);
            }}
            sx={{ color: theme.palette.navigationForeground.main }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            style={
              defaultNavTitlePosition === "CENTER"
                ? {
                    position: "absolute",
                    left: "50%",
                    transform: "translateX(-50%)",
                  }
                : {}
            }
          >
            {titleContent}
          </Box>
        </Stack>
        <Stack direction="row" alignItems={"center"} spacing={2}>
          <Search />
          <CartIcon />
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        borderRadius: "0px",
        justifyContent: "center",
        overflow: "hidden",
        alignContent: "center",
        height: "100%",
        alignItems: "center",
        backgroundColor: "transparent",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        boxShadow: "none",
      }}
      id="drawer-container"
    >
      <MobilePreviewSidepane
        items={defaultNestedMenuItems}
        isFirstRender={isSidepaneFirstRender}
        isOpen={isMenuOpen}
        onClose={() => setMenuOpen(false)}
      />
      <MobilePreviewBottomSheet
        activeShortcut={(() => {
          const item = defaultShortcutsPills[bottomSheetIndex];
          if (!item)
            return { label: "", url: "", localizations: [], children: [] };

          return mapToNavigationItem(item);
        })()}
        isOpen={isBottomSheetOpen}
        isFirstRender={isBottomSheetFirstRender}
        onClose={() => {
          setBottomSheetOpen(false);
        }}
      />
      {isLiquidGlass ? glassHeader : standardHeader}
      <Stack
        direction={"row"}
        width={"100%"}
        spacing={1}
        alignItems={"center"}
        marginTop={isLiquidGlass ? 0 : "-1px"}
        p={1}
        sx={{
          backgroundColor: isLiquidGlass
            ? navBg
            : theme.palette.pillContainerBackground.main,
          position: "sticky",
          top: "56px",
          zIndex: 9,
          overflow: "auto",
          overflowY: "hidden",
          "&::-webkit-scrollbar": {
            height: "4px",
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(0,0,0,0.2)",
            borderRadius: "2px",
          },
        }}
      >
        {shortcutItems}
      </Stack>
      <Box sx={{ flex: 1 }} /> {/* Spacer to push footer to bottom */}
      <Stack
        sx={{
          position: "sticky",
          bottom: 0,
          width: "100%",
          backgroundColor: isLiquidGlass ? "transparent" : "white",
          px: isLiquidGlass ? 1.5 : 0,
          pb: isLiquidGlass ? 1.5 : 0,
        }}
        alignItems={"end"}
      >
        {defaultIsGorgiasEnabled && (
          <IconButton
            sx={
              isLiquidGlass
                ? {
                    m: 1.5,
                    width: "52px",
                    height: "52px",
                    zIndex: 50,
                    ...liquidGlassCapsuleSx(glassColors),
                    "&:hover": {
                      backgroundColor: glassColors.hoverFill,
                    },
                  }
                : {
                    m: 2,
                    width: "52px",
                    height: "52px",
                    zIndex: 50,
                    backgroundColor: theme.palette.primaryButtonBg.main,
                    "&:hover": {
                      backgroundColor:
                        theme.palette.primaryButtonBg.main + "CC",
                    },
                    color: theme.palette.primaryButtonFg.main,
                    boxShadow: "2px 4px 4px 0px rgba(47, 55, 146, 0.15)",
                  }
            }
          >
            <ChatBubbleIcon />
          </IconButton>
        )}
        <Tabs
          centered
          variant="fullWidth"
          sx={{
            width: "100%",
            ...(isLiquidGlass
              ? {
                  ...liquidGlassCapsuleSx(glassColors, {
                    borderRadius: "28px",
                    padding: "4px 0",
                  }),
                  minHeight: 56,
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }
              : {
                  backgroundColor: theme.palette.navigationBackground.main,
                  color: theme.palette.navigationForeground.main,
                }),
          }}
          textColor="inherit"
          TabIndicatorProps={
            isLiquidGlass
              ? { sx: { display: "none" } }
              : {
                  sx: {
                    top: 0,
                    backgroundColor: theme.palette.navigationForeground.main,
                  },
                }
          }
          value={value}
          onChange={(_event: React.SyntheticEvent, newValue: number) => {
            setValue(newValue);
          }}
        >
          {defaultTabs.map((tab, index) => {
            const Icon = getBrandIcon(tab.icon);
            const isSelected = value === index;

            if (isLiquidGlass) {
              return (
                <Tab
                  key={index}
                  sx={{
                    textTransform: "none",
                    padding: "4px 2px",
                    opacity: isSelected ? 1 : 0.7,
                    "&.MuiButtonBase-root.MuiTab-root": {
                      flex: "1 1 0",
                      minWidth: "0",
                      minHeight: 48,
                    },
                    "& .MuiTab-iconWrapper": {
                      margin: 0,
                    },
                  }}
                  icon={
                    <Box
                      sx={{
                        ...(isSelected
                          ? liquidGlassCapsuleSx(glassColors, {
                              borderRadius: "999px",
                              padding: defaultIsTitleEnabled
                                ? "6px 18px 4px"
                                : "8px 14px",
                              selected: true,
                            })
                          : {
                              padding: defaultIsTitleEnabled
                                ? "6px 12px 4px"
                                : "8px",
                            }),
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                        aspectRatio:
                          defaultIsTitleEnabled || isSelected ? undefined : "1",
                        minWidth: defaultIsTitleEnabled
                          ? undefined
                          : isSelected
                          ? 48
                          : 40,
                        minHeight: 40,
                      }}
                    >
                      <Icon size={22} color="currentColor" />
                      {defaultIsTitleEnabled && (
                        <Typography
                          sx={{
                            fontSize: "10px",
                            fontWeight: 800,
                            lineHeight: 1.2,
                          }}
                        >
                          {tab.customName}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              );
            }

            return (
              <Tab
                sx={{
                  textTransform: "none",
                  padding: 0,

                  "&.MuiButtonBase-root.MuiTab-root": {
                    flex: "1 1 0",
                    minWidth: "0",
                  },
                }}
                key={index}
                label={
                  defaultIsTitleEnabled ? (
                    <Typography
                      sx={{
                        fontSize: "10px",
                        fontWeight: 800,
                      }}
                    >
                      {tab.customName}
                    </Typography>
                  ) : undefined
                }
                icon={<Icon size={24} color="currentColor" />}
              />
            );
          })}
        </Tabs>
      </Stack>
    </Box>
  );
};

export default NavigationPreview;
